/**
 * The `watcher` SourceClient (sources.md "watcher — push only"). For platforms with no usable public API
 * (Facebook, LinkedIn, X) and for any webhook. Items arrive only through `engine.sources.push` — from the
 * server's `/api/push/<token>` on the web, from a watcher feeder in the later native apps. The scheduler
 * never pulls it (`pull_minutes = 0`), so `fetch` refuses with `unsupported`.
 *
 *   config  { platform, target }          locator  watcher:<platform>:<foldText(target)>
 */

import type { SourceClient, SourceInput, SourceResolution, WatcherConfig } from '../contracts/sources.js';
import { DigesterError } from '../kernel/errors.js';
import { foldText } from '../kernel/text.js';

const PLATFORMS: ReadonlyArray<WatcherConfig['platform']> = ['facebook', 'linkedin', 'x', 'webhook', 'other'];
const PLATFORM_NAMES: Readonly<Record<WatcherConfig['platform'], string>> = {
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  x: 'X',
  webhook: 'Webhook',
  other: 'Watcher',
};

function isPlatform(value: unknown): value is WatcherConfig['platform'] {
  return typeof value === 'string' && (PLATFORMS as readonly string[]).includes(value);
}

/** The canonical locator of a watcher source. */
export function watcherLocator(platform: WatcherConfig['platform'], target: string): string {
  return `watcher:${platform}:${foldText(target)}`;
}

export const watcherClient: SourceClient<'watcher'> = {
  kind: 'watcher',
  defaultPullMinutes: 0,

  async resolve(input: SourceInput<'watcher'>): Promise<SourceResolution<'watcher'>> {
    const platform = input.config?.platform ?? 'webhook';
    if (!isPlatform(platform)) {
      throw new DigesterError('invalid_input', `Choose what is watched: ${PLATFORMS.join(', ')}`, { issues: [{ path: 'config.platform', message: 'unknown platform' }] });
    }
    const target = (input.config?.target ?? input.address).trim();
    if (foldText(target) === '') {
      throw new DigesterError('invalid_input', 'Say which page, profile or list is watched', { issues: [{ path: 'address', message: 'required' }] });
    }
    return {
      kind: 'watcher',
      locator: watcherLocator(platform, target),
      name: `${PLATFORM_NAMES[platform]}: ${target}`.slice(0, 80),
      config: { kind: 'watcher', platform, target },
      requiredSecrets: [],
    };
  },

  async fetch() {
    throw new DigesterError('unsupported', 'A watcher source is fed by push only; there is nothing to fetch.');
  },
};
